import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getAutomations, createAutomation, updateAutomation, deleteAutomation,
  executeAutomation, getAutomationLog, getAutomationStats
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Zap, Plus, Trash2, Edit, Play, History, BarChart3,
  Bell, ListTodo, Megaphone, Clock, Power, PowerOff
} from 'lucide-react';

const TRIGGER_TYPES = [
  { id: 'evento_creado', label: 'Evento Creado' },
  { id: 'cuota_vencida', label: 'Cuota Vencida' },
  { id: 'usuario_registrado', label: 'Usuario Registrado' },
  { id: 'cron', label: 'Cron (Programada)' },
  { id: 'manual', label: 'Manual' },
];

const ACTION_TYPES = [
  { id: 'notificacion', label: 'Enviar Notificación', icon: Bell },
  { id: 'crear_tarea', label: 'Crear Tarea', icon: ListTodo },
  { id: 'crear_anuncio', label: 'Crear Anuncio', icon: Megaphone },
];

export default function Automations() {
  const { hasPermission } = useAuth();
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, total_runs: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('rules');
  const [showDialog, setShowDialog] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [form, setForm] = useState({
    nombre: '', descripcion: '', activa: true,
    trigger_type: 'manual', trigger_config: {},
    conditions: [], actions: [{ tipo: 'notificacion', config: { titulo: '', mensaje: '' } }]
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [rulesRes, statsRes] = await Promise.all([
        getAutomations(), getAutomationStats()
      ]);
      setRules(rulesRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchLogs = async () => {
    try {
      const res = await getAutomationLog();
      setLogs(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!form.nombre) return toast.error('El nombre es requerido');
    try {
      await createAutomation(form);
      toast.success('Automatización creada');
      setShowDialog(false);
      resetForm();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear'); }
  };

  const handleUpdate = async () => {
    try {
      await updateAutomation(editRule.id, form);
      toast.success('Automatización actualizada');
      setShowDialog(false);
      setEditRule(null);
      resetForm();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al actualizar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta automatización?')) return;
    try {
      await deleteAutomation(id);
      toast.success('Automatización eliminada');
      fetchAll();
    } catch (err) { toast.error('Error al eliminar'); }
  };

  const handleExecute = async (id) => {
    try {
      const res = await executeAutomation(id);
      toast.success(res.data.message);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al ejecutar'); }
  };

  const handleToggle = async (rule) => {
    try {
      await updateAutomation(rule.id, { activa: !rule.activa });
      toast.success(rule.activa ? 'Desactivada' : 'Activada');
      fetchAll();
    } catch (err) { toast.error('Error'); }
  };

  const openEdit = (rule) => {
    setEditRule(rule);
    setForm({
      nombre: rule.nombre, descripcion: rule.descripcion || '',
      activa: rule.activa, trigger_type: rule.trigger_type,
      trigger_config: rule.trigger_config || {},
      conditions: rule.conditions || [],
      actions: rule.actions?.length ? rule.actions : [{ tipo: 'notificacion', config: { titulo: '', mensaje: '' } }]
    });
    setShowDialog(true);
  };

  const resetForm = () => setForm({
    nombre: '', descripcion: '', activa: true,
    trigger_type: 'manual', trigger_config: {},
    conditions: [], actions: [{ tipo: 'notificacion', config: { titulo: '', mensaje: '' } }]
  });

  const updateAction = (idx, field, value) => {
    const newActions = [...form.actions];
    if (field === 'tipo') {
      newActions[idx] = { tipo: value, config: {} };
    } else {
      newActions[idx] = { ...newActions[idx], config: { ...newActions[idx].config, [field]: value } };
    }
    setForm({ ...form, actions: newActions });
  };

  const addAction = () => {
    setForm({ ...form, actions: [...form.actions, { tipo: 'notificacion', config: { titulo: '', mensaje: '' } }] });
  };

  const removeAction = (idx) => {
    setForm({ ...form, actions: form.actions.filter((_, i) => i !== idx) });
  };

  if (!hasPermission('automatizaciones', 'ver')) {
    return <div className="text-center py-20 text-stone-500" data-testid="access-denied">Acceso denegado</div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="automations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Automatizaciones</h1>
          <p className="text-stone-500 text-sm mt-1">Motor de reglas IF/THEN</p>
        </div>
        {hasPermission('automatizaciones', 'crear') && (
          <Button onClick={() => { setShowDialog(true); resetForm(); setEditRule(null); }} data-testid="create-automation-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Regla
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card data-testid="stat-total">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100"><Zap className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.total}</p>
              <p className="text-xs text-stone-500">Total Reglas</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-active">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100"><Power className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.active}</p>
              <p className="text-xs text-stone-500">Activas</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-runs">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100"><BarChart3 className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.total_runs}</p>
              <p className="text-xs text-stone-500">Ejecuciones</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'log') fetchLogs(); }}>
        <TabsList data-testid="automation-tabs">
          <TabsTrigger value="rules" data-testid="tab-rules">
            <Zap className="w-4 h-4 mr-1.5" /> Reglas
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">
            <History className="w-4 h-4 mr-1.5" /> Historial
          </TabsTrigger>
        </TabsList>

        {/* Rules Tab */}
        <TabsContent value="rules">
          {rules.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-stone-400">No hay reglas creadas. Crea tu primera automatización.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {rules.map(rule => (
                <Card key={rule.id} className="hover:shadow-md transition-shadow" data-testid={`rule-${rule.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-stone-800">{rule.nombre}</h3>
                          <Badge className={rule.activa ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}>
                            {rule.activa ? 'Activa' : 'Inactiva'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {TRIGGER_TYPES.find(t => t.id === rule.trigger_type)?.label || rule.trigger_type}
                          </Badge>
                        </div>
                        {rule.descripcion && <p className="text-sm text-stone-500">{rule.descripcion}</p>}
                        <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                          <span>{rule.actions?.length || 0} acciones</span>
                          <span>{rule.run_count || 0} ejecuciones</span>
                          {rule.last_run && <span>Última: {new Date(rule.last_run).toLocaleString('es-ES')}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(rule)} data-testid={`toggle-${rule.id}`}>
                          {rule.activa ? <PowerOff className="w-4 h-4 text-stone-400" /> : <Power className="w-4 h-4 text-emerald-500" />}
                        </Button>
                        {hasPermission('automatizaciones', 'editar') && (
                          <Button variant="ghost" size="sm" onClick={() => handleExecute(rule.id)} data-testid={`execute-${rule.id}`}>
                            <Play className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        {hasPermission('automatizaciones', 'editar') && (
                          <Button variant="ghost" size="sm" onClick={() => openEdit(rule)} data-testid={`edit-rule-${rule.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {hasPermission('automatizaciones', 'eliminar') && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(rule.id)} data-testid={`delete-rule-${rule.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Log Tab */}
        <TabsContent value="log">
          <Card>
            <CardHeader><CardTitle className="text-lg">Historial de Ejecuciones</CardTitle></CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-stone-400 text-center py-8">No hay registros de ejecución</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-stone-500">
                        <th className="pb-3 font-medium">Fecha</th>
                        <th className="pb-3 font-medium">Usuario</th>
                        <th className="pb-3 font-medium">Acción</th>
                        <th className="pb-3 font-medium">ID Regla</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, i) => (
                        <tr key={i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`log-row-${i}`}>
                          <td className="py-3 text-xs text-stone-500">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString('es-ES') : '-'}
                          </td>
                          <td className="py-3 text-stone-700">{log.user_name || '-'}</td>
                          <td className="py-3">
                            <Badge variant="outline" className="text-xs">{log.accion}</Badge>
                          </td>
                          <td className="py-3 text-xs text-stone-400 font-mono">{log.registro_id?.slice(0, 8)}...</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={() => { setShowDialog(false); setEditRule(null); resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editRule ? 'Editar Automatización' : 'Nueva Automatización'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Recordatorio semanal" data-testid="auto-nombre" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Descripción</Label>
                <Textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={2} placeholder="Descripción de la regla..." />
              </div>
            </div>

            {/* Trigger */}
            <div className="space-y-2">
              <Label>Tipo de Trigger</Label>
              <Select value={form.trigger_type} onValueChange={v => setForm({ ...form, trigger_type: v })}>
                <SelectTrigger data-testid="auto-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.trigger_type === 'cron' && (
              <div className="space-y-2">
                <Label>Expresión Cron</Label>
                <Input
                  value={form.trigger_config.cron || ''}
                  onChange={e => setForm({ ...form, trigger_config: { ...form.trigger_config, cron: e.target.value } })}
                  placeholder="0 9 * * 1 (Lunes a las 9:00)"
                  data-testid="auto-cron"
                />
                <p className="text-xs text-stone-400">Formato: min hora dia mes dia_semana</p>
              </div>
            )}

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <Switch checked={form.activa} onCheckedChange={v => setForm({ ...form, activa: v })} data-testid="auto-activa" />
              <Label>Regla activa</Label>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Acciones (THEN)</Label>
                <Button variant="outline" size="sm" onClick={addAction} data-testid="add-action-btn">
                  <Plus className="w-3 h-3 mr-1" /> Acción
                </Button>
              </div>
              {form.actions.map((action, idx) => (
                <Card key={idx} className="border-dashed">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Select value={action.tipo} onValueChange={v => updateAction(idx, 'tipo', v)}>
                        <SelectTrigger className="w-[200px]" data-testid={`action-type-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ACTION_TYPES.map(a => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {form.actions.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeAction(idx)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      )}
                    </div>

                    {action.tipo === 'notificacion' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Título</Label>
                          <Input value={action.config.titulo || ''} onChange={e => updateAction(idx, 'titulo', e.target.value)} placeholder="Título de la notificación" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Mensaje</Label>
                          <Input value={action.config.mensaje || ''} onChange={e => updateAction(idx, 'mensaje', e.target.value)} placeholder="Contenido del mensaje" />
                        </div>
                      </div>
                    )}

                    {action.tipo === 'crear_tarea' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Título de Tarea</Label>
                          <Input value={action.config.titulo || ''} onChange={e => updateAction(idx, 'titulo', e.target.value)} placeholder="Título de la tarea" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Descripción</Label>
                          <Input value={action.config.descripcion || ''} onChange={e => updateAction(idx, 'descripcion', e.target.value)} placeholder="Descripción" />
                        </div>
                      </div>
                    )}

                    {action.tipo === 'crear_anuncio' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Título del Anuncio</Label>
                          <Input value={action.config.titulo || ''} onChange={e => updateAction(idx, 'titulo', e.target.value)} placeholder="Título" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Contenido</Label>
                          <Input value={action.config.contenido || ''} onChange={e => updateAction(idx, 'contenido', e.target.value)} placeholder="Contenido del anuncio" />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); setEditRule(null); }}>Cancelar</Button>
            <Button onClick={editRule ? handleUpdate : handleCreate} data-testid="submit-automation-btn">
              {editRule ? 'Actualizar' : 'Crear Regla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
